import {
  TextBody,
  TextDisplay,
  MetricCard,
  AmountChangePercent,
  CtaBanner,
  FundSubscriptionsRedemptions,
  FundHoldings,
  Button,
  useModal,
  FundContactForm,
  useBreakpoints,
  Popover,
  AmountPrice,
  PageContainer,
  Separator,
} from '@xbto/universal-components';
import { FC, useCallback, useEffect, useMemo } from 'react';
import { useEffectOnce } from 'react-use';
import { useNavigate } from 'react-router-dom';
import { APP_ROUTES } from '~/routes';
import { DataStore } from '~/store';
import {
  ArrayCollection,
  DATE_FORMATS,
  EnrichedAccountDetail,
  EnrichedAccountDetailAsset,
  formatters,
  useTrackEvent,
} from 'common';
import cx from 'classnames';
import { API } from '@xbto/api-client';
import { useExtraNavCta } from '@xbto/universal-components';
import { useCtrlClickNavigation } from '~/hooks/use-ctrl-click-navigation';

/**
 * TODO (@jey) - this can be done in common on a later date
 */
const _getHoldingsTableSubtitle = (acDetail: EnrichedAccountDetail | null) => {
  if (!acDetail || !acDetail.isAccountOfTypeFund) {
    return null;
  }
  const result: string[] = [];
  if (acDetail.dataAsOf) {
    result.push(
      `As of ${formatters.getDate(acDetail.dataAsOf, DATE_FORMATS.DD_MMM_YYYY, true)}.`
    );
  }
  if (acDetail.dataLastUpdatedAsOf) {
    result.push(
      `Last updated on ${formatters.getDate(acDetail.dataLastUpdatedAsOf, DATE_FORMATS.DD_MMM_YYYY, true)}.`
    );
  }
  if (!result.length) {
    return null;
  }
  return result.join(' ');
};

export const AccountDetail_ExperienceFunds: FC = () => {
  /**
   * Store
   */
  const accessToken = DataStore.useStoreState(s => s.user.accessToken);
  const accountDetail = DataStore.useStoreState(s => s.portfolio.accountDetail);
  const identity = DataStore.useStoreState(s => s.user.identity);
  const funds = DataStore.useStoreState(s => s.markets.funds);
  const relationshipManager = DataStore.useStoreState(
    s => s.settings.relationshipManager
  );

  const getFunds = DataStore.useStoreActions(a => a.markets.getFunds);

  /**
   * Hooks
   */
  const navigate = useNavigate();
  const trackEvent = useTrackEvent();
  const { breakpoints } = useBreakpoints(500);
  const { setExtraNavCta } = useExtraNavCta();
  const { Modal: InvestNowModal, open, close } = useModal();

  /**
   * Methods
   */
  const onViewOurFunds = useCallback(() => {
    trackEvent('view-funds_cta_clicked', { source: 'banner' });
    navigate(`${APP_ROUTES.AUTH_MARKETS}?assetType=Funds`);
  }, [navigate, trackEvent]);

  const handleBeforeSubmit = useCallback(() => {
    trackEvent('contact-form_send-cta_clicked', { source: 'invest_flow' });
  }, [trackEvent]);

  const onInvestNowPressed = useCallback(async () => {
    if (identity) {
      trackEvent('fund-subscription_cta_clicked', {
        source: 'funds-holdings-page_invest-now',
      });
      open({
        Body: (
          <FundContactForm
            accessToken={accessToken}
            funds={funds ?? []}
            identity={identity}
            manager={relationshipManager}
            mode="invest"
            onBeforeSubmit={handleBeforeSubmit}
            onClose={close}
          />
        ),
      });
    }
  }, [
    accessToken,
    close,
    funds,
    handleBeforeSubmit,
    identity,
    open,
    relationshipManager,
    trackEvent,
  ]);

  const investNowCta = useMemo(
    () => ({
      label: 'Invest now',
      variant: 'primary' as const,
      size: 'sm' as const,
      onPress: onInvestNowPressed,
    }),
    [onInvestNowPressed]
  );

  /**
   * Effects
   */
  useEffectOnce(() => {
    getFunds();
  });

  useEffect(() => {
    setExtraNavCta(breakpoints.LESS_THAN_M ? investNowCta : null);
    return () => {
      setExtraNavCta(null);
    };
  }, [breakpoints.LESS_THAN_M, investNowCta, setExtraNavCta]);

  /**
   * DOM
   */
  const provisionalOnlyAccount = useMemo(() => {
    return !!(
      accountDetail?.assets.length === 0 &&
      (accountDetail?.subscriptions.length > 0 ||
        accountDetail?.redemptions.length > 0)
    );
  }, [accountDetail]);

  const totalBalanceInUsd = useMemo(() => {
    if (provisionalOnlyAccount) {
      const coll = new ArrayCollection<API.ProvisionalAccountDetailAsset>();
      coll.push(...(accountDetail?.provisionalAssets || []));
      return formatters.getBaseCurrency(coll.sumOf('valueUsd'));
    }
    return accountDetail?.formatted.totalBalanceIncludingPendingUsd;
  }, [accountDetail, provisionalOnlyAccount]);

  const ctrlClickNavigation = useCtrlClickNavigation();
  const handleRowPress = useCallback(
    (asset: EnrichedAccountDetailAsset, event) => {
      trackEvent('funds-holdings_row-item_clicked');
      const assetCode = asset.fundDetails?.asset?.code;
      if (!assetCode || !accountDetail?.account) {
        return;
      }
      const path = APP_ROUTES.AUTH_PORTFOLIO_HOLDING.replace(
        ':id',
        accountDetail.account.accountId
      ).replace(':currencyCode', assetCode);
      if (!ctrlClickNavigation(event, path)) {
        navigate(path);
      }
    },
    [accountDetail?.account, ctrlClickNavigation, navigate, trackEvent]
  );

  if (!accountDetail) {
    return null; // this case is already handled in the parent component
  }

  const metricCards = [
    {
      label: 'Last monthly performance (USD)',
      value: accountDetail.lastMonthlyPerformance,
      Component: AmountChangePercent,
    },
    {
      label: 'Year to date (USD)',
      value: accountDetail.yearToDate,
      Component: AmountChangePercent,
    },
    {
      label: 'Inception to date (USD)',
      value: accountDetail.inceptionToDate,
      Component: AmountChangePercent,
    },
    {
      label: 'Gain/loss since first investment (USD)',
      value: accountDetail.gainOrLossSinceFirstInvestment,
      signed: true,
      Component: AmountPrice,
    },
  ];

  const subTitle = _getHoldingsTableSubtitle(accountDetail);

  return (
    <PageContainer>
      <>
        <div className="flex flex-col flex-1">
          {/* Section: Totals */}
          <div className="flex flex-col pt-6 gap-6 sm:gap-8">
            <div className="flex flex-row justify-between items-start">
              <div className="flex flex-col gap-4">
                <div className="flex items-center">
                  <TextBody lhFull>
                    {provisionalOnlyAccount
                      ? 'Provisional holdings'
                      : 'Asset management holdings'}
                  </TextBody>
                  {accountDetail.account?.accountName && (
                    <>
                      <Separator />
                      <TextBody lhFull>
                        {accountDetail.account.accountName.toUpperCase()}
                      </TextBody>
                    </>
                  )}
                </div>
                {/* Note: by using text display we are losing the animated amount, @hadrien or @benoit should check with design/ product if that is ok! */}
                {provisionalOnlyAccount ? (
                  <Popover
                    trigger={
                      <TextDisplay
                        small={breakpoints.XS}
                        color="disabled"
                        style={{ display: 'flex' }}
                      >
                        {totalBalanceInUsd}
                      </TextDisplay>
                    }
                    placement="bottom-end"
                  >
                    <TextBody color="primary">
                      Your holdings balance is still pending
                    </TextBody>
                  </Popover>
                ) : (
                  <TextDisplay small={breakpoints.XS}>
                    {totalBalanceInUsd}
                  </TextDisplay>
                )}
                {!provisionalOnlyAccount && (
                  <TextBody lhFull>excluding provisional holdings</TextBody>
                )}
              </div>
              {breakpoints.MORE_THAN_M && <Button {...investNowCta} />}
            </div>
            <div>
              <div
                className={cx(
                  'grid gap-2 w-full max-w-full mb-3',
                  breakpoints.LESS_THAN_S ? 'grid-rows-4' : 'grid-cols-4'
                )}
              >
                {metricCards.map(
                  ({ label, value, Component, ...rest }, index) => (
                    <div className="" key={index}>
                      <MetricCard label={label}>
                        {provisionalOnlyAccount ? (
                          <TextBody color="disabled" lhFull>
                            -
                          </TextBody>
                        ) : (
                          <Component value={value} em lhFull {...rest} />
                        )}
                      </MetricCard>
                    </div>
                  )
                )}
              </div>
              <div className="mb-4">
                <TextBody lhFull small>
                  Performance figures above are computed using USD-counter value
                  of the assets you own as the calculation basis
                </TextBody>
              </div>
            </div>
          </div>

          {!!accountDetail.assets.length && (
            <div className="pt-8">
              <FundHoldings
                rowData={accountDetail.assets}
                subTitle={subTitle}
                trackPerformanceInClicked={() => {
                  trackEvent('fund-performance-in_toggle_clicked', {
                    source: 'funds-holdings-table',
                  });
                }}
                onRowPress={handleRowPress}
              />
            </div>
          )}

          {!!(
            accountDetail.subscriptions.length ||
            accountDetail.redemptions.length
          ) && (
            <div className="pt-10">
              <FundSubscriptionsRedemptions
                subscriptionsRowData={accountDetail.subscriptions}
                redemptionsRowData={accountDetail.redemptions}
              />
            </div>
          )}
        </div>

        <div className="pt-8 mt-auto">
          <CtaBanner
            title="Looking to invest in more funds?"
            text="We have a collection of other funds across multiple asset classes you might be interested in"
            variant="info"
            ctaLabel="View our funds"
            onCtaPress={onViewOurFunds}
          />
        </div>
      </>
      <InvestNowModal />
    </PageContainer>
  );
};
